import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Boxes, Factory, Image, Settings, Mail, Users, LogOut, Loader2, ExternalLink, Menu } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ImzLogo } from "@/components/Brand";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard", roles: ["super_admin", "content_manager", "event_operator"] },
  { to: "/admin/solutions", label: "Solutions", icon: Boxes, testId: "admin-nav-solutions", roles: ["super_admin", "content_manager"] },
  { to: "/admin/industries", label: "Industries", icon: Factory, testId: "admin-nav-industries", roles: ["super_admin", "content_manager"] },
  { to: "/admin/media", label: "Media Library", icon: Image, testId: "admin-nav-media", roles: ["super_admin", "content_manager"] },
  { to: "/admin/leads", label: "Leads & Analytics", icon: Mail, testId: "admin-nav-leads", roles: ["super_admin", "content_manager", "event_operator"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings", roles: ["super_admin"] },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users", roles: ["super_admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-[#050b18]"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  const items = NAV.filter((n) => n.roles.includes(user.role));

  const SideContent = () => (
    <>
      <div className="px-5 py-6"><ImzLogo /></div>
      <nav className="flex-1 space-y-1 px-3">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={n.testId}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
              isActive ? "cyan-grad text-[#041021]" : "text-slate-300 hover:bg-cyan-500/10 hover:text-white"
            )}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-2 border-t border-slate-800 p-3">
        <a href="/kiosk" target="_blank" rel="noreferrer" data-testid="admin-open-kiosk"
          className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-cyan-200 hover:bg-cyan-500/10">
          <ExternalLink className="h-5 w-5" /> Open Kiosk
        </a>
        <div className="rounded-xl bg-[#0b1730] px-4 py-3">
          <p className="truncate text-sm font-medium text-white">{user.name}</p>
          <p className="text-xs capitalize text-cyan-300">{user.role.replace("_", " ")}</p>
        </div>
        <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout"
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-rose-300 hover:bg-rose-500/10">
          <LogOut className="h-5 w-5" /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#050b18] text-white">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-800 bg-[#070f22] lg:flex">
        <SideContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-[#070f22]"><SideContent /></aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-800 px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle" className="rounded-lg p-2 text-cyan-200"><Menu className="h-6 w-6" /></button>
          <ImzLogo />
        </header>
        <main className="flex-1 overflow-y-auto p-5 sm:p-8"><Outlet context={{ user }} /></main>
      </div>
    </div>
  );
}
